// src/components/header/AppHeader.tsx
// UPDATED: Unified glass header - same bg-white/80 backdrop-blur-xl for top bar and context band
// UPDATED: Normalized heights (72px top, 40px context) and padding (px-6)
// UPDATED: Default upgradeDestination changed to /ea/upgrade

import HeaderLogo from "./HeaderLogo";
import HeaderSearch from "./HeaderSearch";
import HeaderUpgradeCard from "./HeaderUpgradeCard";
import HeaderNotifications from "./HeaderNotifications";
import HeaderUserMenu from "./HeaderUserMenu";
import { PageContextRow } from "./PageContextRow";
import { useScroll } from "../../hooks/useScroll";

type Plan = "freemium" | "pro";

interface Breadcrumb {
  label: string;
  href?: string;
}

interface AppHeaderProps {
  workspaceTitle?: string;
  plan: Plan;
  onSearch?: (q: string) => void;
  user: { name: string; avatarUrl?: string };
  variant?: "glass" | "gradient";
  headline?: string;
  contextTitle: string;
  breadcrumbs?: Breadcrumb[];
  tabs?: string[];
  activeTab?: "Tools" | "Documents" | "Messages";
  onTabChange?: (tab: "Tools" | "Documents" | "Messages") => void;
  messageCount?: number;
  upgradeDestination?: string;
  onOpenNotifications?: () => void;
}

export default function AppHeader({
  workspaceTitle = "Data Value Accelerator",
  plan,
  onSearch,
  user,
  variant = "glass",
  headline = "",
  contextTitle,
  breadcrumbs,
  tabs,
  activeTab = "Tools",
  onTabChange,
  messageCount = 0,
  upgradeDestination = "/ea/upgrade",  // CHANGED: from "/freemium/upgrade"
  onOpenNotifications,
}: AppHeaderProps) {
  const scrolled = useScroll(20);

  // Unified glass background with dynamic shadows on scroll
  const headerClasses = [
    "sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b transition-all duration-300",
    scrolled
      ? "border-slate-300 shadow-[0_4px_6px_rgba(0,0,0,.1),0_12px_24px_rgba(24,54,94,.15),0_20px_40px_rgba(24,54,94,.1)]"
      : "border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,.04),0_4px_12px_rgba(24,54,94,.06)]",
  ].join(" ");

  return (
    <header className={headerClasses} role="banner">
      {/* Optional gradient accent bar */}
      {variant === "gradient" && (
        <div className="h-1 bg-gradient-to-r from-[#18365E] to-[#FF6E4C]" aria-hidden="true" />
      )}
      
      {/* Main row (72px) - Unified height and padding */}
      <div className="h-[72px] px-6 flex items-center gap-4">
        {/* Left: Logo + Title */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="p-2 rounded-lg bg-white/60 shadow-sm">
            <HeaderLogo />
          </div>
          <div className="text-lg font-semibold text-slate-900 whitespace-nowrap">
            {workspaceTitle}
          </div>
        </div>

        {/* Center: Search */}
        <div className="hidden md:block flex-1 max-w-2xl mx-auto">
          <HeaderSearch onSearch={onSearch} />
        </div>

        {/* Right: Welcome Message (Pro) / Upgrade (Freemium) + User */}
        <div className="ml-auto flex items-center gap-3 flex-shrink-0">
          {plan === "freemium" ? (
            <HeaderUpgradeCard destination={upgradeDestination} />
          ) : (
            <span className="hidden md:block text-sm text-slate-700" style={{ fontWeight: 500 }}>
              Welcome back, {user.name}!
            </span>
          )}
          <HeaderNotifications unreadCount={messageCount} onOpen={onOpenNotifications} />
          <HeaderUserMenu user={user} />
        </div>
      </div>

      {/* Context band - Only show if breadcrumbs exist (non-Pro Journey pages) */}
      {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
        <div className="border-t border-slate-200">
          <PageContextRow
            headline={headline}
            title={contextTitle}
            breadcrumbs={breadcrumbs}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange ? (t: string) => {
              if (t === "Tools" || t === "Documents" || t === "Messages") {
                onTabChange(t);
              }
            } : undefined}
            messageCount={messageCount}
          />
        </div>
      )}
    </header>
  );
}